import React, { useState } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Box,
  Container,
  CircularProgress,
} from "@material-ui/core";
import {
  makeStyles,
  createTheme,
  ThemeProvider,
} from "@material-ui/core/styles";
import { useNavigate, useLocation } from "react-router-dom";

import LanguageHelper from "../../helpers/LanguageHelper";
import SessionHelper from "../../helpers/SessionHelper";
import Request from "../../helpers/Request";

import logo from "../../assets/images/pincident.png";
import Copyright from "../../components/Footer/Copyright";
import CustomSnackbar from "../../components/Snackbar/Snackbar";
import Settings from "../../components/Buttons/Settings";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: 20,
    width: 300,
    height: "auto",
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(2, 0, 2),
  },
  buttonContainer: {
    marginTop: 10,
    textAlign: "center",
    paddingBottom: 20,
  },
}));

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX =
  /(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%&*'(),\-+<=>:;?{}^._]).{8,32}$/;

export default function SignIn({ update, setUpdate }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const language = LanguageHelper.getLanguage();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [snackbar, setSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem("darkMode"));
  const [, setLanguage] = useState(language);

  const theme = createTheme({
    palette: { type: darkMode === "true" ? "dark" : "light" },
  });

  const handleLogin = async () => {
    setLoading(true);
    const resp = await Request(
      "post",
      "/api/auth/signin",
      {
        username: username,
        password: password,
      },
      null,
      {
        "Accept-Language": "tr-TR",
        "Strict-Transport-Security": "max-age=31536000; includeSubDomains",
        "X-Frame-Options": "DENY",
        "X-XSS-Protection": "1; mode=block",
        "X-Content-Type-Options": "nosniff",
        "Cache-Control":
          "private, max-age=0, no-cache, no-store, must-revalidate",
        "Referrer-Policy": "no-strict-origin-when-cross-origin",
        "Content-Security-Policy":
          "default-src 'self'; script-src 'self'; style-src 'self'; font-src 'self'; img-src 'self'; frame-src 'self'; upgrade-insecure-requests",
      }
    );
    console.log(resp);
    if (resp?.status === 200) {
      const data = resp.data.content;
      if (data.isLoggedIn) {
        SessionHelper.setUser(data.jwt);
        setUpdate(!update);

        const redirectPath = location?.state?.from?.pathname || "/dashboard";
        navigate(redirectPath, {
          ...(PASSWORD_REGEX.test(password) ? {} : { state: { error: true } }),
        });
      } else {
        navigate("/verification", {
          state: {
            password: password,
            key: data.key,
            message: resp.data.messageResponse.message,
          },
        });
      }
    } else {
      handleLoginError(resp);
    }

    setLoading(false);
  };

  const handleLoginError = (resp) => {
    setSeverity("error");
    setSnackbar(true);
    if (resp?.status === 429) {
      setSnackbarMessage(
        "Çok fazla istekte bulundunuz. Lütfen daha sonra tekrar deneyiniz."
      );
    } else if (resp?.data?.messageResponse?.message) {
      setSnackbarMessage(resp.data.messageResponse.message);
    } else {
      setSnackbarMessage(language.login.unexpectedError);
    }
  };

  const handleSubmit = () => {
    if (!username) {
      showSnackbar(language.login.provideEmail);
    } else if (!password) {
      showSnackbar(language.login.providePassword);
    } else if (!EMAIL_REGEX.test(username)) {
      showSnackbar(language.login.weirdEmail);
    } else {
      handleLogin();
    }
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbar(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  const renderLoginButton = () => (
    <div className={classes.buttonContainer}>
      {loading ? (
        <CircularProgress color="secondary" />
      ) : (
        <Button
          type="button"
          fullWidth
          variant="contained"
          color="default"
          className={classes.submit}
          onClick={handleSubmit}
        >
          {language.login.signin}
        </Button>
      )}
    </div>
  );

  return (
    <Container maxWidth={false}>
      <CustomSnackbar
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        snackbarMessage={snackbarMessage}
        severity={severity}
        disableHide={true}
      />
      <Settings
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        setLanguage={setLanguage}
        isLoginPage
      />
      <Container component="main" maxWidth="xs">
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div className={classes.paper} onKeyDown={handleKeyDown}>
            <img className={classes.avatar} src={logo} alt="logo" />
            <form className={classes.form} noValidate>
              <TextField
                error={severity === "error"}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                label={language.login.email}
                autoFocus
                onChange={(e) => setUsername(e.target.value)}
                onBlur={(e) => setUsername(e.target.value.trim())}
              />
              <TextField
                error={severity === "error"}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                label={language.login.password}
                type="password"
                onChange={(password) => setPassword(password.target.value)}
              />
              {renderLoginButton()}
            </form>
          </div>
          <Box mt={8}>
            <Copyright
              string={"DELTA Smart Technologies"}
              date={new Date().getFullYear()}
              url={"https://deltasmart.tech/en/home/"}
            />
          </Box>
        </ThemeProvider>
      </Container>
    </Container>
  );
}
